import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectorRef, NgZone } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ApiService } from "src/app/services/api.service";
import { LanguageService } from "src/app/services/language.service";
import { RoutingService } from "src/app/services/routing.service";
import { Observable, Subject, Subscription, takeUntil } from "rxjs";
import { Entity } from "src/app/models/mvp-query-response-dto";
import { EntityType } from "src/app/enums/entity-type.enum";
import { ExcavationPageDto, ExcavationFileDto } from "src/app/models/excavation-page-dto";
import { ControlType } from '../arcgis-map/constants/control-type.enum';
import { MapControlItem } from '../arcgis-map/constants/map-control-item.interface';
import { ARCGIS_MAP_LIFECYCLE } from '../arcgis-map/constants/map-life-cycle.interface';
import { MainMapService } from '../arcgis-map/services/main-map.service';

@Component({
  selector: 'excavation',
  templateUrl: './excavation.component.html',
  styleUrls: ['./excavation.component.scss'],
  providers: [
    {
      provide: ARCGIS_MAP_LIFECYCLE,
      useClass: MainMapService,
    },
  ],
})
export class ExcavationComponent implements OnInit, OnDestroy {    
    private entityDetailsSubscription?: Subscription;
    private languageSubscription?: Subscription;
    private currentLanguageSubscription?: Subscription;
    closeClicked: Subject<void> = new Subject<void>();
    switchToMapTab: Subject<void> = new Subject<void>();
    @Output() onShapeLeftClick = new EventEmitter<any[]>();
    @Output() onClose = new EventEmitter<void>();
    selectedExcavationDetails!: ExcavationPageDto;
    excavationDetailsIsLoading: boolean = true;
    entityId: number = -1;
    entity: Entity = {} as Entity;
    entityType: string = EntityType.EXCAVATION;
    currentLanguage$!: Observable<string>;
    currentLang: string = 'he';
    currentLanguage: 'he' | 'en' = 'he';
    shapes: any[] = [];
    mapControls: MapControlItem[] = [];
    selectedEntityId: number | null = null;
    highlightedShapeId: string | null = null;

  constructor(
     private routingService: RoutingService,
     private apiService: ApiService,
     private translate: TranslateService,
     private languageService: LanguageService,
     private cdr: ChangeDetectorRef,
     private ngZone: NgZone) {
  }

  ngOnInit(): void {
    this.currentLanguage$ = this.languageService.currentLanguage$;

    this.languageSubscription = this.currentLanguage$.subscribe(lang => {
      this.currentLang = lang;
    });

    this.currentLanguageSubscription = this.languageService.currentLanguage$.subscribe((lang) => {
      this.currentLanguage = lang as 'he' | 'en';
      this.populateMapControls();
    });

    this.entityDetailsSubscription = this.routingService.entityDetails$
      .subscribe(entityDetails => {
        this.ngZone.run(() => {
          const { id, type } = entityDetails;
          if (!!id && [EntityType.EXCAVATION].includes(type as EntityType)) {
            this.entityId = id;
            this.entityType = type;
            this.cdr.detectChanges();
            this.getExcavationDetails(id);
          }
        });
      });
  }

  getExcavationDetails(entityId: number) {
    this.excavationDetailsIsLoading = true;
    this.apiService.getExcavationPage(entityId)
      .pipe(takeUntil(this.closeClicked))
      .subscribe({
        next: (res: ExcavationPageDto) => {
          this.selectedExcavationDetails = res;
          this.buildShapes();
          this.stopLoading();
        },
        error: () => this.stopLoading(),
        complete: () => {
          this.stopLoading(); 
        }
      });
  }

  buildShapes() {
    this.shapes = [];
    
    if (this.selectedExcavationDetails?.mapInfo) {
      const mapInfo = this.selectedExcavationDetails.mapInfo;
      
      // Add publication polygons
      mapInfo.publicationShapes?.forEach((shape, index) => {
        const pub = this.selectedExcavationDetails.publications.find(p => p.entityId === shape.shapeData?.entity?.entityId);
        const isHighlighted = this.selectedEntityId === shape.shapeData?.entity?.entityId;
        
        let titleText = '';
        if (pub?.title) {
          if (typeof pub.title === 'string') {
            titleText = pub.title;
          } else if (typeof pub.title === 'object') {
            titleText = this.currentLang === 'he' 
              ? (pub.title.hebrew || pub.title.english || '')
              : (pub.title.english || pub.title.hebrew || '');
          }
        }
        
        this.shapes.push({
          wkt: shape.shapeWkt,
          layerName: this.translate.instant('iaa.app.excavation.publicationLayer'),
          shapeId: `pub-${shape.shapeData?.entity?.entityId}`,
          hidden: false,
          shapeData: shape.shapeData,
          popupTemplate: {
            title: titleText || 'Publication'
          },
          styledParams: {
            fillColor: isHighlighted ? [255, 200, 100, 0.5] : [255, 200, 100, 0.3],
            borderColor: isHighlighted ? [200, 150, 50, 1] : [200, 150, 50, 0.8],
            borderWidth: isHighlighted ? 3 : 2,
          }
        });
      });

      // Add file polygons
      mapInfo.fileShapes?.forEach((shape, index) => {
        const file = this.selectedExcavationDetails.excavationFiles.find((f: ExcavationFileDto) => f.entityId === shape.shapeData?.entity?.entityId);
        const isHighlighted = this.selectedEntityId === shape.shapeData?.entity?.entityId;
        
        // Handle multilingual fileName - ensure we get a string
        let fileName = '';
        if (file?.fileName) {
          if (typeof file.fileName === 'string') {
            fileName = file.fileName;
          } else if (typeof file.fileName === 'object') {
            fileName = this.currentLang === 'he' 
              ? (file.fileName.hebrew || file.fileName.english || '')
              : (file.fileName.english || file.fileName.hebrew || '');
          }
        }
        
        this.shapes.push({
          wkt: shape.shapeWkt,
          layerName: this.translate.instant('iaa.app.excavation.fileLayer'),
          shapeId: `file-${shape.shapeData?.entity?.entityId}`,
          hidden: false,
          shapeData: shape.shapeData,
          popupTemplate: {
            title: fileName || 'File'
          },
          styledParams: {
            fillColor: isHighlighted ? [150, 255, 150, 0.5] : [150, 255, 150, 0.3],
            borderColor: isHighlighted ? [100, 200, 100, 1] : [100, 200, 100, 0.8],
            borderWidth: isHighlighted ? 3 : 2,
          }
        });
      });

      // Add model polygons
      mapInfo.modelShapes?.forEach((shape, index) => {
        const model = this.selectedExcavationDetails.models.find(m => m.entityId === shape.shapeData?.entity?.entityId);
        const isHighlighted = this.selectedEntityId === shape.shapeData?.entity?.entityId;
        
        // Handle multilingual modelName - ensure we get a string
        let modelName = '';
        if (model?.modelName) {
          if (typeof model.modelName === 'string') {
            modelName = model.modelName;
          } else if (typeof model.modelName === 'object') {
            modelName = this.currentLang === 'he' 
              ? (model.modelName.hebrew || model.modelName.english || '')
              : (model.modelName.english || model.modelName.hebrew || '');
          }
        }
        
        this.shapes.push({
          wkt: shape.shapeWkt,
          layerName: this.translate.instant('iaa.app.excavation.modelLayer'),
          shapeId: `model-${shape.shapeData?.entity?.entityId}`,
          hidden: false,
          shapeData: shape.shapeData,
          popupTemplate: {
            title: modelName || 'Model'
          },
          styledParams: {
            fillColor: isHighlighted ? [255, 150, 255, 0.5] : [255, 150, 255, 0.3],
            borderColor: isHighlighted ? [200, 100, 200, 1] : [200, 100, 200, 0.8],
            borderWidth: isHighlighted ? 3 : 2,
          }
        });
      });

      // Add report polygons
      mapInfo.reportShapes?.forEach((shape, index) => {
        const report = this.selectedExcavationDetails.yazamReports.find(r => r.entityId === shape.shapeData?.entity?.entityId);
        const isHighlighted = this.selectedEntityId === shape.shapeData?.entity?.entityId;
        
        // Handle multilingual reportTitle - ensure we get a string
        let reportTitle = '';
        if (report?.reportTitle) {
          if (typeof report.reportTitle === 'string') {
            reportTitle = report.reportTitle;
          } else if (typeof report.reportTitle === 'object') {
            reportTitle = this.currentLang === 'he' 
              ? (report.reportTitle.hebrew || report.reportTitle.english || '')
              : (report.reportTitle.english || report.reportTitle.hebrew || '');
          }
        }
        
        this.shapes.push({
          wkt: shape.shapeWkt,
          layerName: this.translate.instant('iaa.app.excavation.reportLayer'),
          shapeId: `report-${shape.shapeData?.entity?.entityId}`,
          hidden: false,
          shapeData: shape.shapeData,
          popupTemplate: {
            title: reportTitle || 'Report'
          },
          styledParams: {
            fillColor: isHighlighted ? [144, 238, 144, 0.5] : [144, 238, 144, 0.3],
            borderColor: isHighlighted ? [60, 179, 113, 1] : [60, 179, 113, 0.8],
            markerSize: isHighlighted ? 14 : 10,
            markerOutlineColor: [60, 179, 113, 1],
            markerOutlineWidth: isHighlighted ? 3 : 2,
          }
        });
      });

      // Add declared sites
      mapInfo.declaredSites?.forEach(shape => {
        // Handle translate.instant result - ensure we get a string
        let siteLabel = this.translate.instant('iaa.app.excavation.declaredSite');
        if (typeof siteLabel === 'object') {
          siteLabel = this.currentLang === 'he'
            ? (siteLabel.hebrew || siteLabel.english || 'Declared Site')
            : (siteLabel.english || siteLabel.hebrew || 'Declared Site');
        }
        
        this.shapes.push({
          wkt: shape.shapeWkt,
          layerName: this.translate.instant('iaa.app.excavation.declaredSiteLayer'),
          shapeId: `site-${shape.shapeId}`,
          hidden: false,
          shapeData: shape.shapeData || {},
          popupTemplate: {
            title: siteLabel || 'Declared Site'
          },
          styledParams: {
            fillColor: [200, 200, 200, 0.15],
            borderColor: [100, 100, 100, 0.8],
            borderWidth: 1.5,
          }
        });
      });

      // Add license shapes
      this.selectedExcavationDetails.licenses?.forEach((license: any) => {
        license.shapes?.forEach((shape: any) => {
          const isHighlighted = this.selectedEntityId === license.licenseId;

          this.shapes.push({
            wkt: shape.wkt,
            layerName: this.translate.instant('iaa.app.excavation.licenseLayer'),
            shapeId: `license-${license.licenseId}-${shape.shapeId}`,
            hidden: false,
            shapeData: shape.shapeData || {},
            popupTemplate: {
              title: license.licenseLongName || license.licenseShortName || 'License'
            },
            styledParams: {
              fillColor: isHighlighted ? [255, 200, 100, 0.5] : [255, 200, 100, 0.3],
              borderColor: isHighlighted ? [204, 102, 0, 1] : [204, 102, 0, 0.8],
              borderWidth: isHighlighted ? 2 : 1.5,
            }
          });
        });
      });
            // Add excavation polygon (merged from all license shapes)
      if (mapInfo.excavationPolygon) {
        const isHighlighted = this.selectedEntityId === this.selectedExcavationDetails.entityId;
        
        // Handle excavation name - ensure we get a string
        let excavationTitle = this.currentLang === 'he' 
          ? (this.selectedExcavationDetails.excavationNameHebrew || this.selectedExcavationDetails.excavationNameEnglish)
          : (this.selectedExcavationDetails.excavationNameEnglish || this.selectedExcavationDetails.excavationNameHebrew);
        
        this.shapes.push({
          wkt: mapInfo.excavationPolygon.shapeWkt,
          layerName: this.translate.instant('iaa.app.excavation.excavationLayer'),
          shapeId: `excavation-${this.selectedExcavationDetails.excavationId}`,
          hidden: false,
          shapeData: { 
            ...mapInfo.excavationPolygon.shapeData,
            entity: {
              entityId: this.selectedExcavationDetails.entityId,
              entityType: 'excavation',
              title: {
                hebrew: this.selectedExcavationDetails.excavationNameHebrew,
                english: this.selectedExcavationDetails.excavationNameEnglish
              }
            }
          },
          popupTemplate: {
            title: excavationTitle || 'Excavation'
          },
          styledParams: {
            fillColor: isHighlighted ? [100, 150, 255, 0.4] : [100, 150, 255, 0.2],
            borderColor: isHighlighted ? [50, 100, 200, 1] : [50, 100, 200, 0.9],
            borderWidth: isHighlighted ? 4 : 3,
          }
        });
      }
    }
  }

  createExcavationPopupContent(): string {
    const parts: string[] = [];
    
    if (this.selectedExcavationDetails.publications?.length > 0) {
      parts.push(`<div><strong>${this.translate.instant('iaa.app.excavation.publications')}:</strong> ${this.selectedExcavationDetails.publications.length}</div>`);
    }
    if (this.selectedExcavationDetails.excavationFiles?.length > 0) {
      parts.push(`<div><strong>${this.translate.instant('iaa.app.excavation.danaPdfs')}:</strong> ${this.selectedExcavationDetails.excavationFiles.length}</div>`);
    }
    if (this.selectedExcavationDetails.models?.length > 0) {
      parts.push(`<div><strong>${this.translate.instant('iaa.app.excavation.models')}:</strong> ${this.selectedExcavationDetails.models.length}</div>`);
    }
    if (this.selectedExcavationDetails.yazamReports?.length > 0) {
      parts.push(`<div><strong>${this.translate.instant('iaa.app.excavation.yazamReports')}:</strong> ${this.selectedExcavationDetails.yazamReports.length}</div>`);
    }
    
    return parts.join('') || this.translate.instant('iaa.app.excavation.excavationArea');
  }

  stopLoading() {
    this.excavationDetailsIsLoading = false;
  }

  highlightShape(entityId: number, entityType: string) {
    // Switch to map tab on mobile
    this.switchToMapTab.next();
    
    // Toggle behavior: if clicking the same entity, deselect it
    if (this.selectedEntityId === entityId) {
      this.selectedEntityId = null;
    } else {
      this.selectedEntityId = entityId;
    }
    
    // Rebuild shapes to apply highlighting
    this.buildShapes();
  }

  
  populateMapControls(): void {
    const position = this.currentLanguage === 'he' ? 'top-left' : 'top-right';
    this.mapControls = [
      { position: position, type: ControlType.FullScreen },
      { position: position, type: ControlType.Zoom },
      {
        position: position,
        type: ControlType.Locate,
        attributes: { scale: 100 },
      },
      {
        position: position,
        type: ControlType.BasemapGallery,
        expand: {
          id: 'basemap-gallery-expand',
          position: position,
          expandIcon: 'basemap',
          expandTooltip: `${this.translate.instant('iaa.map.expand-basemap-gallery')}`,
          mode: 'floating',
        },
      },
      {
        position: position,
        type: ControlType.LayerList,
        expand: {
          id: 'layer-list-expand',
          position: position,
          expandIcon: 'layers',
          expandTooltip: `${this.translate.instant('iaa.map.expand-layers')}`,
          mode: 'floating',
        },
      },
      {
        position: position,
        type: ControlType.Print,
        attributes: {
          allowedFormats: 'all',
          allowedLayouts: 'all',
          printServiceUrl:
            'https://gis.israntique.org.il/arcgis/rest/services/IAAPrintTemplates/IAAPrintTemplateGrid25000/GPServer/Export%20Web%20Map',
        },
        expand: {
          position: position,
          expandIcon: 'print',
          expandTooltip: `${this.translate.instant('iaa.map.expand-print')}`,
          mode: 'floating',
        },
      },
      {
        position: position,
        type: ControlType.AreaMeasurement2D,
        attributes: {
          unit: 'metric',
          locale: this.currentLanguage
        },
        expand: {
          position: position,
          expandIcon: 'polygon',
          expandTooltip: `${this.translate.instant('iaa.map.expand-area-measurement')}`,
          mode: 'floating',
        },
      },
      {
        position: position,
        type: ControlType.DistanceMeasurement2D,
        attributes: {
          unit: 'metric',
          locale: this.currentLanguage
        },
        expand: {
          position: position,
          expandIcon: 'measure-line',
          expandTooltip: `${this.translate.instant('iaa.map.expand-distance-measurement')}`,
          mode: 'floating',
        },
      },
      {
        position: this.currentLanguage === 'he' ? 'bottom-right' : 'bottom-left',
        type: ControlType.CoordinateConversion
      },
    ];
  }

  ngOnDestroy(): void {
    this.entityDetailsSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
    this.currentLanguageSubscription?.unsubscribe();
  }

  goToExcavations(): void {
    const excavationPolygon = this.selectedExcavationDetails?.mapInfo?.excavationPolygon?.shapeWkt;
    if (!excavationPolygon) {
      console.warn('No excavation polygon available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'excavations',
      regions: excavationPolygon,
      tabId: 0,
      entity_index: 0
    });
  }

  goToLociBaskets(): void {
    const excavationId = this.selectedExcavationDetails?.excavationId;
    if (!excavationId) return;
    
    this.routingService['navigateWithReplace']({
      excavations: excavationId,
      entityTypes: 'loci,baskets',
      tabId: 0,
      entity_index: 0
    });
  }

  goToSites(): void {
    const excavationPolygon = this.selectedExcavationDetails?.mapInfo?.excavationPolygon?.shapeWkt;
    if (!excavationPolygon) {
      console.warn('No excavation polygon available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'hachrazot',
      regions: excavationPolygon,
      tabId: 0,
      entity_index: 0
    });
  }

  goToObjects(): void {
    const licenses = this.selectedExcavationDetails?.licenses?.map(l => l.licenseId).join(',');
    if (!licenses) {
      console.warn('No licenses available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'objects',
      excavationLicenses: licenses,
      tabId: 0,
      entity_index: 0
    });
  }

  goToArchiveDocuments(): void {
    const licenses = this.selectedExcavationDetails?.licenses?.map(l => l.licenseId).join(',');
    if (!licenses) {
      console.warn('No licenses available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'scientific_archive_documents',
      excavationLicenses: licenses,
      tabId: 0,
      entity_index: 0
    });
  }

    goToSurveys(): void {
    const excavationPolygon = this.selectedExcavationDetails?.mapInfo?.excavationPolygon?.shapeWkt;
    if (!excavationPolygon) {
      console.warn('No excavation polygon available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'surveys',
      regions: excavationPolygon,
      tabId: 0,
      entity_index: 0
    });
  }

  getDetailsTitle(): string {
    const prefix = this.translate.instant('iaa.app.excavation.title');
    
    const title = this.currentLang === 'he' 
      ? (this.selectedExcavationDetails?.excavationNameHebrew || this.selectedExcavationDetails?.excavationNameEnglish || '')
      : (this.selectedExcavationDetails?.excavationNameEnglish || this.selectedExcavationDetails?.excavationNameHebrew || '');
    
    return title ? `${prefix} - ${title}` : prefix;
  }

  getMapTitle(): string {
    return this.translate.instant('iaa.app.excavation.mapTitle');
  }
  hasExcavationPolygon(): boolean {
    return !!this.selectedExcavationDetails?.mapInfo?.excavationPolygon?.shapeWkt;
  }
}
